import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar, Clock, MapPin, User, Phone, AlertCircle, CheckCircle2, XCircle, HourglassIcon, Video, Loader2 } from 'lucide-react';
import { appointmentService, getLocalizedValue } from '@/services/api';
import { Link, useNavigate } from 'react-router-dom';
import { format, isWithinInterval, addMinutes, subMinutes } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

// Appointment status mapping based on appointment_status_id
const APPOINTMENT_STATUS = {
  1: 'pending',      // Request
  2: 'confirmed',    // Accepted
  3: 'in_progress',  // In Clinic
  4: 'completed',    // Done
  5: 'cancelled',    // Cancelled
  6: 'rejected',     // Rejected
  7: 'waiting',      // Waiting
};

const AppointmentsPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { startConversation, startCall } = useChat();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [apiError, setApiError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const lang = i18n.language || 'ar';
  
  // Ref to prevent duplicate cancel submissions
  const isCancelInProgress = useRef(false);

  // Check if appointment is within call window (15 min before to 30 min after start)
  const canJoinCall = (appointment) => {
    if (!appointment.online) return false;
    
    const now = new Date();
    const appointmentTime = new Date(appointment.appointment_at);
    const windowStart = subMinutes(appointmentTime, 15);
    const windowEnd = addMinutes(appointmentTime, 30);
    
    return isWithinInterval(now, { start: windowStart, end: windowEnd });
  };

  // Handle joining video call
  const handleJoinCall = async (appointment) => {
    try {
      // Start or get conversation with doctor
      const conversationId = await startConversation(appointment.doctor.id, 'doctor');
      
      // Navigate to messages and initiate call
      navigate('/messages');
      
      // Give time for navigation then start call
      setTimeout(() => {
        startCall('video');
      }, 1000);
    } catch (error) {
      console.error('Failed to join call:', error);
      toast.error(lang === 'ar' ? 'فشل في الانضمام للمكالمة' : 'Failed to join call');
    }
  };

  // Handle cancel appointment
  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedAppointment) return;
    
    // CRITICAL: Prevent duplicate cancel submissions using ref
    if (isCancelInProgress.current) {
      console.log('Cancel already in progress, ignoring duplicate click');
      return;
    }
    
    // Set ref BEFORE state to prevent any race conditions
    isCancelInProgress.current = true;
    setCancelling(true);
    
    try {
      const response = await appointmentService.cancel(selectedAppointment.id);
      
      // Check if backend returned success
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to cancel');
      }
      
      // Update local state to reflect the cancellation
      setAppointments(prev => prev.map(apt => 
        apt.id === selectedAppointment.id 
          ? { ...apt, status: 'cancelled', cancel: 1 }
          : apt
      ));
      
      toast.success(
        lang === 'ar' ? 'تم إلغاء الموعد بنجاح' : 'Appointment cancelled successfully'
      );
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      // Extract error message from response if available
      const status = error.response?.status;
      const errorMsg = error.response?.data?.message || error.message;
      
      // Handle rate limiting specifically
      if (status === 429) {
        toast.error(
          lang === 'ar' 
            ? 'الرجاء الانتظار قبل المحاولة مرة أخرى' 
            : 'Please wait before trying again'
        );
      } else {
        toast.error(
          lang === 'ar' 
            ? (errorMsg || 'فشل في إلغاء الموعد') 
            : (errorMsg || 'Failed to cancel appointment')
        );
      }
    } finally {
      setCancelling(false);
      // Reset ref after a delay to prevent rapid re-clicks
      setTimeout(() => {
        isCancelInProgress.current = false;
      }, 2000);
    }
  };

  // Handle reschedule - navigate to doctor's booking page
  const handleReschedule = (appointment) => {
    if (appointment.doctor?.id) {
      // Navigate to doctor detail page with reschedule flag
      navigate(`/doctors/${appointment.doctor.id}?reschedule=${appointment.id}`);
    } else {
      toast.error(
        lang === 'ar' ? 'لا يمكن إعادة الجدولة' : 'Cannot reschedule this appointment'
      );
    }
  };

  // Redirect if not authenticated - wait for auth to finish loading first
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setApiError(null);
      
      try {
        // Use getAllAppointments to fetch all appointment statuses
        // This mimics the Flutter app behavior which fetches by status
        const response = await appointmentService.getAllAppointments();
        
        // Check if response contains an error (backend bug returns error as JSON)
        if (response?.data?.exception || response?.data?.message?.includes('Undefined array key')) {
          console.log('Backend returned error in response - treating as no appointments');
          setAppointments([]);
          return;
        }
        
        // Ensure we always get an array
        const data = response?.data?.data || response?.data;
        const rawAppointments = Array.isArray(data) ? data : [];
        
        // Transform appointments to have consistent structure
        const transformedAppointments = rawAppointments.map(apt => ({
          id: apt.id,
          doctor: {
            id: apt.doctor?.id,
            name: getLocalizedValue(apt.doctor?.name, lang),
            image: apt.doctor?.media?.[0]?.url || apt.doctor?.media?.[0]?.thumb,
            specialty: apt.doctor?.specialties?.[0] ? {
              name: getLocalizedValue(apt.doctor.specialties[0].name, lang)
            } : null,
          },
          clinic: apt.clinic ? {
            id: apt.clinic.id,
            name: getLocalizedValue(apt.clinic.name, lang),
            phone: apt.clinic.phone_number,
            address: apt.address?.address || '',
          } : null,
          patient: apt.patient ? {
            name: `${getLocalizedValue(apt.patient.first_name, lang)} ${getLocalizedValue(apt.patient.last_name, lang)}`,
          } : null,
          appointment_at: apt.appointment_at,
          start_at: apt.start_at,
          ends_at: apt.ends_at,
          status: APPOINTMENT_STATUS[apt.appointment_status_id] || 'pending',
          is_waiting: apt.is_waiting_appointment,
          online: apt.online,
          total: apt.total,
          cancel: apt.cancel,
        }));
        
        setAppointments(transformedAppointments);
      } catch (error) {
        console.error('Failed to fetch appointments:', error);
        // Handle ANY error gracefully - including backend bugs
        console.log('API error occurred - treating as no appointments');
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !authLoading) {
      fetchAppointments();
    }
  }, [isAuthenticated, authLoading, lang]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: AlertCircle, label: t('appointments.pending') },
      confirmed: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle2, label: t('appointments.confirmed') },
      in_progress: { color: 'bg-purple-100 text-purple-700', icon: HourglassIcon, label: lang === 'ar' ? 'في العيادة' : 'In Progress' },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: t('appointments.completed') },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: t('appointments.cancelledStatus') },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: lang === 'ar' ? 'مرفوض' : 'Rejected' },
      waiting: { color: 'bg-orange-100 text-orange-700', icon: HourglassIcon, label: lang === 'ar' ? 'انتظار' : 'Waiting' },
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return (
      <Badge className={`${config.color} gap-1`}>
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filterAppointments = (tab) => {
    // Ensure appointments is an array
    if (!Array.isArray(appointments)) return [];
    
    const now = new Date();
    switch (tab) {
      case 'upcoming':
        return appointments.filter(apt => 
          new Date(apt.appointment_at) >= now && 
          !['cancelled', 'rejected', 'completed'].includes(apt.status)
        );
      case 'past':
        return appointments.filter(apt => 
          new Date(apt.appointment_at) < now ||
          apt.status === 'completed'
        );
      case 'cancelled':
        return appointments.filter(apt => 
          ['cancelled', 'rejected'].includes(apt.status)
        );
      default:
        return appointments;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'ar' ? ar : enUS;
    return format(date, 'EEEE, MMMM d, yyyy', { locale });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'h:mm a');
  };

  const AppointmentCard = ({ appointment }) => (
    <Card className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`appointment-card-${appointment.id}`}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          <img
            src={appointment.doctor?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.doctor?.name || 'D')}&background=0F6D77&color=fff`}
            alt={appointment.doctor?.name}
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">{appointment.doctor?.name || (lang === 'ar' ? 'دكتور' : 'Doctor')}</h3>
                {appointment.doctor?.specialty?.name && (
                  <p className="text-sm text-teal-600">{appointment.doctor.specialty.name}</p>
                )}
              </div>
              {getStatusBadge(appointment.status)}
            </div>
            
            <div className="space-y-1.5 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>{formatDate(appointment.appointment_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                <span>{formatTime(appointment.appointment_at)}</span>
              </div>
              {appointment.clinic?.name && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span className="truncate">{appointment.clinic.name}</span>
                </div>
              )}
              {appointment.total > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-teal-600 font-medium">{appointment.total} SYP</span>
                </div>
              )}
              {appointment.online && (
                <div className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-blue-500" />
                  <span className="text-blue-600">{lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Join Call Button for Online Appointments */}
        {appointment.online && canJoinCall(appointment) && (
          <div className="mt-4 pt-4 border-t">
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => handleJoinCall(appointment)}
              data-testid="join-call-btn"
            >
              <Video className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'انضم للمكالمة الآن' : 'Join Video Call Now'}
            </Button>
          </div>
        )}
        
        {!['cancelled', 'rejected', 'completed'].includes(appointment.status) && (
          <div className="flex gap-2 mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => handleReschedule(appointment)}
              data-testid="reschedule-btn"
            >
              {t('appointments.reschedule')}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleCancelClick(appointment)}
              data-testid="cancel-btn"
            >
              {t('appointments.cancelAppointment')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="appointments-page">
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('appointments.title')}</h1>
              <p className="text-slate-600 dark:text-slate-400">{t('appointments.subtitle') || 'Manage your healthcare appointments'}</p>
            </div>
            <Button className="bg-teal-600 hover:bg-teal-700" asChild>
              <Link to="/doctors">{t('appointments.bookNew')}</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              {t('appointments.upcoming')}
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              {t('appointments.past')}
            </TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">
              {t('appointments.cancelled')}
            </TabsTrigger>
          </TabsList>

          {['upcoming', 'past', 'cancelled'].map((tab) => (
            <TabsContent key={tab} value={tab} className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <Skeleton className="w-16 h-16 rounded-xl" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-1/3" />
                            <Skeleton className="h-4 w-1/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filterAppointments(tab).length === 0 ? (
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-8 text-center">
                    <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">
                      {tab === 'upcoming' && (t('appointments.noUpcoming') || 'No upcoming appointments')}
                      {tab === 'past' && (t('appointments.noPast') || 'No past appointments')}
                      {tab === 'cancelled' && (t('appointments.noCancelled') || 'No cancelled appointments')}
                    </p>
                    {tab === 'upcoming' && (
                      <Button className="mt-4 bg-teal-600 hover:bg-teal-700" asChild>
                        <Link to="/doctors">{t('appointments.bookNew')}</Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                filterAppointments(tab).map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Cancel Appointment Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-white">
              {lang === 'ar' ? 'إلغاء الموعد' : 'Cancel Appointment'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {lang === 'ar' 
                ? 'هل أنت متأكد من رغبتك في إلغاء هذا الموعد؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to cancel this appointment? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="py-4 border-y dark:border-slate-700">
              <div className="flex items-center gap-3">
                <img
                  src={selectedAppointment.doctor?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAppointment.doctor?.name || 'D')}&background=0F6D77&color=fff`}
                  alt={selectedAppointment.doctor?.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <p className="font-medium dark:text-white">{selectedAppointment.doctor?.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {formatDate(selectedAppointment.appointment_at)} - {formatTime(selectedAppointment.appointment_at)}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
              className="dark:border-slate-600"
            >
              {lang === 'ar' ? 'تراجع' : 'Keep Appointment'}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {lang === 'ar' ? 'جاري الإلغاء...' : 'Cancelling...'}
                </>
              ) : (
                lang === 'ar' ? 'إلغاء الموعد' : 'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
